import { clearCache } from '@/global';
import Logger from '@/services/Logger';
import { ExclamationCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { Button, Result, Typography } from 'antd';
import { Component, ErrorInfo, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

const { Title } = Typography;

interface IState {
    unknownError: boolean;
    chunkError: boolean;
}

interface IProps {
    children: ReactNode;
}

class ErrorBoundary extends Component<IProps, IState> {
    state: IState;

    constructor(props: IProps) {
        super(props);

        this.refreshHandler = this.refreshHandler.bind(this);

        this.state = {
            unknownError: false,
            chunkError: false,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        const { pathname } = window.location;

        Logger.startSession({
            'Location.Pathname': pathname,
        });

        Logger.log_fatal(`Component <ErrorBoundary /> catched this error: ${error}!`);

        if (error.message.includes('Loading chunk') || error.message.includes('Loading CSS chunk')) {
            Logger.log_fatal(`Fatal chunk error: ${error}!`);

            this.setState({ unknownError: false, chunkError: true });

            Logger.endSession();

            return;
        }

        Logger.endSession();

        this.setState({ unknownError: true, chunkError: false });
    }

    private refreshHandler = () => {
        location.reload();
        window.location.href = window.location.href;
        clearCache();
    };

    render() {
        const { unknownError, chunkError } = this.state;

        if (unknownError) {
            return (
                <Result
                    status="error"
                    title={
                        <div style={{ maxWidth: 700, margin: '0 auto' }}>
                            Diçka shkoi keq në sistem. Gabimi është regjistruar dhe dërguar tek departamenti IT! <br /> Ju faleminderit.
                        </div>
                    }
                    extra={
                        <Button type="primary" size="large" onClick={this.refreshHandler}>
                            Rifresko
                        </Button>
                    }
                />
            );
        }

        if (chunkError) {
            return (
                <Result
                    status="info"
                    icon={<ExclamationCircleOutlined style={{ color: '#005CA9' }} />}
                    title={
                        <Title style={{ maxWidth: 700, margin: '0 auto' }} level={3}>
                            <FormattedMessage id="pages.refresh.title" />
                        </Title>
                    }
                    extra={[
                        <Button key="1" size="large" type="primary" onClick={this.refreshHandler} icon={<RedoOutlined style={{ marginRight: 10 }} />}>
                            <FormattedMessage id="pages.refresh.button" />
                        </Button>,
                    ]}
                />
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
